/**
 * DenunciasPage - Gestión de Denuncias
 * Features:
 * - Lista de denuncias
 * - Crear nueva denuncia
 * - Filtros por estado/tipo
 * - Asignar a investigador
 * - Ver detalle y seguimiento
 */
import { useState, useEffect } from 'react';
import { 
  Plus, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  Archive,
  Eye,
  UserCheck,
  Filter,
  Calendar
} from 'lucide-react';
import { denunciasAPI } from '../../api';
import useAuthStore from '../../store/authStore';
import {
  Button,
  Card,
  Table,
  Pagination,
  Loading,
  Alert,
  Modal,
} from '../../components/common';
import DenunciaForm from '../../components/denuncias/DenunciaForm';
import CaseTracker from '../../components/denuncias/CaseTracker';

const DenunciasPage = () => {
  const { user } = useAuthStore();
  const isMiembroComite = user?.es_miembro_comite || user?.rol === 'superadmin' || user?.rol === 'admin_org';

  // Estado
  const [denuncias, setDenuncias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Filtros
  const [filters, setFilters] = useState({
    estado: '',
    tipo: '',
    fecha_desde: '',
    fecha_hasta: '',
  });

  // Modales
  const [showFormModal, setShowFormModal] = useState(false);
  const [showTrackerModal, setShowTrackerModal] = useState(false);
  const [selectedDenuncia, setSelectedDenuncia] = useState(null);

  // Estadísticas
  const [estadisticas, setEstadisticas] = useState(null);

  // Cargar denuncias
  const loadDenuncias = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      // Aplicar filtros
      if (filters.estado) params.estado = filters.estado;
      if (filters.tipo) params.tipo = filters.tipo;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;

      const response = await denunciasAPI.getAll(params);
      
      setDenuncias(response.data.items || response.data);
      setTotalItems(response.data.total || response.data.length);
      setTotalPages(Math.ceil((response.data.total || response.data.length) / itemsPerPage));

      // Cargar estadísticas
      if (isMiembroComite) {
        loadEstadisticas();
      }
    } catch (err) {
      console.error('Error al cargar denuncias:', err);
      setError(err.message || 'Error al cargar denuncias');
    } finally {
      setLoading(false);
    }
  };

  const loadEstadisticas = async () => {
    try {
      const response = await denunciasAPI.getEstadisticas();
      setEstadisticas(response.data);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  useEffect(() => {
    loadDenuncias();
  }, [currentPage, filters]);

  // Handlers
  const handleCrearDenuncia = () => {
    setShowFormModal(true);
  };

  const handleDenunciaCreada = () => {
    setShowFormModal(false);
    setSuccess('Denuncia registrada correctamente');
    loadDenuncias();
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleVerDetalle = (denuncia) => {
    setSelectedDenuncia(denuncia);
    setShowTrackerModal(true);
  };

  const handleAsignar = async (denunciaId, investigadorId) => {
    try {
      setError(null);
      await denunciasAPI.asignar(denunciaId, { id_investigador: investigadorId });
      
      setSuccess('Investigador asignado correctamente');
      loadDenuncias();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al asignar investigador:', err);
      setError(err.message || 'Error al asignar investigador');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      estado: '',
      tipo: '',
      fecha_desde: '',
      fecha_hasta: '',
    });
    setCurrentPage(1);
  };

  // Obtener info de estado
  const getEstadoInfo = (estado) => {
    const estados = {
      nueva: {
        label: 'Nueva',
        color: 'blue',
        icon: AlertTriangle,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
      },
      en_investigacion: {
        label: 'En Investigación',
        color: 'yellow',
        icon: Clock,
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
      },
      resuelta: {
        label: 'Resuelta',
        color: 'green',
        icon: CheckCircle,
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
      },
      archivada: {
        label: 'Archivada',
        color: 'gray',
        icon: Archive,
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
      },
    };

    return estados[estado] || estados.nueva;
  };

  // Obtener prioridad
  const getPrioridadColor = (prioridad) => {
    const prioridades = {
      alta: 'text-red-600 font-bold',
      media: 'text-yellow-600 font-medium',
      baja: 'text-gray-600',
    };
    return prioridades[prioridad] || prioridades.baja;
  };

  // Columnas de la tabla
  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (item) => (
        <span className="font-mono text-sm text-gray-900">
          #{item.id_denuncia}
        </span>
      ),
    },
    {
      key: 'tipo',
      label: 'Tipo',
      render: (item) => (
        <span className="text-sm text-gray-900">
          {item.tipo_denuncia}
        </span>
      ),
    },
    {
      key: 'descripcion',
      label: 'Descripción',
      render: (item) => (
        <p className="text-sm text-gray-700 truncate max-w-xs">
          {item.descripcion}
        </p>
      ),
    },
    {
      key: 'prioridad',
      label: 'Prioridad',
      render: (item) => (
        <span className={`text-sm ${getPrioridadColor(item.prioridad)}`}>
          {item.prioridad?.toUpperCase() || 'MEDIA'}
        </span>
      ),
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (item) => {
        const estadoInfo = getEstadoInfo(item.estado);
        const Icon = estadoInfo.icon;
        
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${estadoInfo.bgColor} ${estadoInfo.textColor}`}
          >
            <Icon className="h-3 w-3 mr-1" />
            {estadoInfo.label}
          </span>
        );
      },
    },
    {
      key: 'fecha',
      label: 'Fecha',
      render: (item) => (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="h-4 w-4" />
          {new Date(item.fecha_creacion).toLocaleDateString('es-ES')}
        </div>
      ),
    },
    {
      key: 'investigador',
      label: 'Investigador',
      render: (item) => {
        if (item.investigador) {
          return (
            <div className="flex items-center gap-1 text-sm">
              <UserCheck className="h-4 w-4 text-green-600" />
              <span className="text-gray-900">{item.investigador.nombre_completo}</span>
            </div>
          );
        }
        return (
          <span className="text-sm text-gray-400">Sin asignar</span>
        );
      },
    },
    {
      key: 'acciones',
      label: 'Acciones',
      render: (item) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleVerDetalle(item)}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Denuncias</h1>
          <p className="mt-1 text-sm text-gray-500">
            Sistema de gestión de denuncias éticas
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleCrearDenuncia}
          icon={Plus}
        >
          Nueva Denuncia
        </Button>
      </div>

      {/* Alertas */}
      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Estadísticas */}
      {estadisticas && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Denuncias</p>
                <p className="text-2xl font-bold">{estadisticas.total_denuncias || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">En Investigación</p>
                <p className="text-2xl font-bold">{estadisticas.en_investigacion || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Resueltas</p>
                <p className="text-2xl font-bold">{estadisticas.resueltas || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Alta Prioridad</p>
                <p className="text-2xl font-bold">{estadisticas.alta_prioridad || 0}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              value={filters.estado}
              onChange={(e) => handleFilterChange({ estado: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Todos</option>
              <option value="nueva">Nuevas</option>
              <option value="en_investigacion">En Investigación</option>
              <option value="resuelta">Resueltas</option>
              <option value="archivada">Archivadas</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={filters.tipo}
              onChange={(e) => handleFilterChange({ tipo: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Todos</option>
              <option value="acoso">Acoso</option>
              <option value="discriminacion">Discriminación</option>
              <option value="conflicto_interes">Conflicto de Interés</option>
              <option value="mal_uso_recursos">Mal Uso de Recursos</option>
              <option value="otro">Otro</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={filters.fecha_desde}
              onChange={(e) => handleFilterChange({ fecha_desde: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={filters.fecha_hasta}
              onChange={(e) => handleFilterChange({ fecha_hasta: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-end">
            <Button variant="outline" onClick={clearFilters} className="w-full">
              Limpiar
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabla de denuncias */}
      <Card>
        {loading ? (
          <div className="p-8">
            <Loading size="lg" text="Cargando denuncias..." />
          </div>
        ) : denuncias.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No hay denuncias registradas</p>
            <Button
              variant="primary"
              onClick={handleCrearDenuncia}
              className="mt-4"
            >
              Crear Primera Denuncia
            </Button>
          </div>
        ) : (
          <>
            <Table columns={columns} data={denuncias} />
            {totalPages > 1 && (
              <div className="border-t p-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Modal: Formulario de Denuncia */}
      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title="Nueva Denuncia"
        size="lg"
      >
        <DenunciaForm
          onSuccess={handleDenunciaCreada}
          onCancel={() => setShowFormModal(false)}
        />
      </Modal>

      {/* Modal: Seguimiento de Caso */}
      {selectedDenuncia && (
        <Modal
          isOpen={showTrackerModal}
          onClose={() => {
            setShowTrackerModal(false);
            setSelectedDenuncia(null);
          }}
          title={`Denuncia #${selectedDenuncia.id_denuncia}`}
          size="xl"
        >
          <CaseTracker
            denunciaId={selectedDenuncia.id_denuncia}
            onUpdate={loadDenuncias}
          />
        </Modal>
      )}
    </div>
  );
};

export default DenunciasPage;
